import {useStore} from "./useStore";
import {useFetch} from "./useFetch";
import {useEffect, useMemo, useState} from "react";
import {message} from "antd";

export const useCodPaymentMethodSelection = ({
    initialCall = true,
    condition = true,
    initialData = [],
    dependencies = [],
    locale = "tr",
    isBackoffice = false
}) => {
    const stores = useStore();

    const [contractLoading, setContractLoading] = useState(false);

    const [contractsMissingFields, setContractsMissingFields] = useState([]);

    const [
        subCompaniesResultsLoading,
        subCompaniesResults,
        setSubCompaniesResults,
        _,
        refetch
    ] = useFetch({
        fetchFn: stores.companyStore.getSubCompanyList.bind(null, {
            limit: 10
        }),
        defaultDataValue: [],
        showLoading: false,
        initialCall: initialCall,
        dependencies: dependencies,
        condition: condition,
        callbackFunc: result => {
            if (Array.isArray(result?.rows)) {
                stores.meStore.setHasCodPaymentMethodSelectionChecked();
            }
        }
    });

    useEffect(() => {
        if (initialData?.length > 0) {
            setSubCompaniesResults({
                rows: initialData || [],
                dataCount: initialData?.length || 0
            });
        }
    }, [initialData, setSubCompaniesResults]);

    /**
     * Cod Payment Method seçimi(sözleşmesi) yapabilecek alt firmalar
     * Seçim özelliği aktif fakat henüz kapıda ödemeyi seçmemiş
     */
    const canCodPaymentMethodSelectionSubcompanies = useMemo(() => {
        return (
            subCompaniesResults?.rows?.filter(
                subCompany =>
                    subCompany.options?.codPayment?.vigoPOSSelfSelectionEnabled &&
                    !subCompany.options?.codPayment?.hasTerminalRight
            ) || []
        );
    }, [subCompaniesResults]);

    /**
     * Cod Payment Method seçimi(sözleşmesi) mutlaka gösterilme durumu
     * Seçim özelliği aktif fakat herhangi bir ödeme yöntemi seçmemiş
     */
    const needToShowCodPaymentMethodSelection = useMemo(() => {
        const subCompanies = subCompaniesResults?.rows?.filter(subCompany => {
            const codPayment = subCompany?.options?.codPayment;

            return (
                codPayment?.vigoPOSSelfSelectionEnabled && !codPayment?.paymentMethodsSelfSelected
            );
        });
        return subCompanies?.length > 0;
    }, [subCompaniesResults]);

    /**
     * Opsiyonel Cod Payment Method seçimi(sözleşmesi) gösterimi
     * Cod Payment Method seçimi(sözleşmesi) yapabilecek alt firmalar var ise
     * gösterime izin verilir
     */
    const canShowCodPaymentMethodSelection = useMemo(() => {
        if (isBackoffice) {
            return false;
        }
        return canCodPaymentMethodSelectionSubcompanies?.length > 0;
    }, [canCodPaymentMethodSelectionSubcompanies, isBackoffice]);

    /**
     * Kapıda ödeme seçimi yapmış alt firmalar
     */
    const alreadyHasCoDPaymentContractSubcompanies = useMemo(() => {
        return subCompaniesResults?.rows?.filter(subCompany => {
            return (
                subCompany?.options?.codPayment?.hasTerminalRight &&
                subCompany?.options?.codPayment?.paymentMethodsSelfSelected
            );
        });
    }, [subCompaniesResults]);

    const generateCoDPaymentContract = async ({subCompanyId, createNewContract = false}) => {
        setContractLoading(true);
        try {
            if (!subCompanyId) {
                setContractLoading(false);
                return {
                    success: false,
                    message: "Şube seçilmedi"
                };
            }

            const contractResult = await stores.contractStore.createAndShowContract({
                subCompanyId,
                contractName: "codAgreement",
                forceCreate: Boolean(createNewContract)
            });
            if (contractResult?.data?.success === false) {
                setContractLoading(false);
                setContractsMissingFields(
                    contractResult?.data?.error?.missingFields?.[locale || "tr"]
                );
                return {
                    success: false,
                    message: contractResult?.error?.message || "Sözleşme oluşturulamadı"
                };
            }
            if (!contractResult?.contractPath) {
                setContractLoading(false);
                return {
                    success: false,
                    message: "Sözleşme verisi alınamadı"
                };
            }

            setContractLoading(false);
            return {
                success: true,
                message: "Sözleşme oluşturuldu",
                contractPath: contractResult?.contractPath,
                contractId: contractResult?.contractId,
                result: contractResult
            };
        } catch (error) {
            setContractLoading(false);
            message.error("Sözleşme oluşturulamadı");
            return {
                success: false,
                message: "Sözleşme oluşturulamadı"
            };
        }
    };
    const getCoDPaymentContract = async ({subCompanyId}) => {
        setContractLoading(true);
        try {
            if (!subCompanyId) {
                setContractLoading(false);
                return {
                    success: false,
                    message: "Şube seçilmedi"
                };
            }

            const contractResult = await stores.contractStore.getContractsBySubCompanyId({
                subCompanyId,
                contractName: "codAgreement"
            });
            if (contractResult?.success === false) {
                setContractLoading(false);
                return {
                    success: false,
                    message: contractResult?.error?.message || "Sözleşme bulunamadı"
                };
            }

            const lastContract = contractResult?.data?.filter(
                contract => contract?.contractName === "codAgreement"
            )?.[0];
            if (!lastContract) {
                setContractLoading(false);
                return {
                    success: false,
                    message: "Sözleşme bulunamadı"
                };
            }
            setContractLoading(false);
            return {
                success: true,
                message: "Sözleşme oluşturuldu",
                contractPath: lastContract?.files?.[0]?.path,
                uploadedDocumentPath: lastContract?.uploadedDocumentPath,
                contractId: lastContract?.contractId,
                result: lastContract
            };
        } catch (error) {
            setContractLoading(false);
            message.error("Sözleşme bulunamadı");
            return {
                success: false,
                message: "Sözleşme bulunamadı"
            };
        }
    };

    return {
        subCompaniesResults,
        subCompaniesResultsLoading,
        canShowCodPaymentMethodSelection,
        needToShowCodPaymentMethodSelection,
        canCodPaymentMethodSelectionSubcompanies,
        contractLoading,
        showContractsMissingFieldsModal: contractsMissingFields?.length > 0,
        contractsMissingFields,
        alreadyHasCoDPaymentContractSubcompanies,
        resetContractsMissingFields: () => setContractsMissingFields([]),
        generateCoDPaymentContract,
        getCoDPaymentContract,
        reloadSubCompaniesResults: refetch
    };
};
